import { create } from 'zustand';
import { onAuthChange, getUserProfile, signInWithGoogle, signOutUser, setupNickname, subscribeToUserProfile } from '../services/authService';
import { initializeRevenueCat, subscribeToCustomerInfoUpdate, getCustomerInfo } from '../services/revenueCatService';
import { syncSubscriptionWithRevenueCat } from '../services/subscriptionService';

const useAuthStore = create((set, get) => ({
    user: null,
    profile: null,
    loading: true,
    isNewUser: false,

    // 인증 상태 초기화
    initialize: () => {
        let profileUnsub = null;
        let rcListenerUnsub = null;
        let activeSessionId = 0; // 세션 구분용 플래그

        const authUnsub = onAuthChange(async (firebaseUser) => {
            activeSessionId++;
            const currentSession = activeSessionId;
            if (profileUnsub) {
                profileUnsub();
                profileUnsub = null;
            }
            if (rcListenerUnsub) {
                try { rcListenerUnsub.remove(); } catch (e) { }
                rcListenerUnsub = null;
            }

            if (firebaseUser) {
                // ★ 프로필 구독 (필수 — 즉시 실행)
                let isFirstLoad = true;
                let profileReceived = false;
                profileUnsub = subscribeToUserProfile(firebaseUser.uid, (profile) => {
                    profileReceived = true;
                    set({
                        user: firebaseUser,
                        profile: profile,
                        loading: false,
                        isNewUser: !profile,
                    });

                    // FCM 토큰 자동 등록 — 3초 지연 (비필수, 메인 스레드 양보)
                    if (isFirstLoad && profile?.notificationSettings?.enabled !== false) {
                        isFirstLoad = false;
                        setTimeout(() => {
                            import('../services/notificationService').then(({ registerPushNotifications }) => {
                                registerPushNotifications(firebaseUser.uid);
                            }).catch((e) => console.warn('FCM 등록 실패:', e));
                        }, 3000);
                    }
                });

                // ★ RevenueCat 초기화 — 3초 지연 (비필수, 메인 스레드 양보)
                // 첫 실행 시 네이티브 브릿지 동시 호출이 메인 스레드를 프리징시키므로 지연
                setTimeout(() => {
                    if (currentSession !== activeSessionId) return;
                    initializeRevenueCat(firebaseUser.uid).then(async () => {
                        if (currentSession !== activeSessionId) return;

                        if (firebaseUser?.uid) {
                            const latestInfo = await getCustomerInfo();
                            if (latestInfo) {
                                await syncSubscriptionWithRevenueCat(firebaseUser.uid, latestInfo);
                            }
                        }

                        rcListenerUnsub = subscribeToCustomerInfoUpdate((customerInfo) => {
                            console.log('RevenueCat CustomerInfo updated:', customerInfo);
                            if (firebaseUser?.uid) {
                                syncSubscriptionWithRevenueCat(firebaseUser.uid, customerInfo);
                            }
                        });
                    }).catch(e => console.warn('RevenueCat 초기화 체인 실패 (비필수):', e));
                }, 3000);

                // ★ 10초 타임아웃: profile 콜백이 아직 안 왔으면 getDoc 강제 폴백
                setTimeout(async () => {
                    if (!profileReceived && currentSession === activeSessionId) {
                        console.warn('프로필 구독 10초 타임아웃 — getDoc 폴백 시도');
                        try {
                            const profile = await getUserProfile(firebaseUser.uid);
                            if (currentSession === activeSessionId) {
                                set({
                                    user: firebaseUser,
                                    profile: profile,
                                    loading: false,
                                    isNewUser: !profile,
                                });
                            }
                        } catch (e) {
                            console.error('프로필 타임아웃 폴백 실패:', e);
                            if (currentSession === activeSessionId) {
                                set({ user: firebaseUser, profile: null, loading: false, isNewUser: true });
                            }
                        }
                    }
                }, 10000);
            } else {
                set({
                    user: null,
                    profile: null,
                    loading: false,
                    isNewUser: false,
                });
            }
        });

        return () => {
            authUnsub();
            if (profileUnsub) {
                profileUnsub();
                profileUnsub = null;
            }
            if (rcListenerUnsub) {
                try { rcListenerUnsub.remove(); } catch (e) { }
                rcListenerUnsub = null;
            }
        };
    },

    // Google 로그인
    login: async () => {
        try {
            const { user, isNewUser } = await signInWithGoogle();

            if (isNewUser) {
                set({ isNewUser: true, user });
            }
        } catch (error) {
            console.error('로그인 실패:', error);
            throw error;
        }
    },

    // 닉네임 설정 (신규 사용자)
    setNickname: async (nickname) => {
        const { user } = get();
        if (!user) throw new Error('로그인이 필요합니다.');

        await setupNickname(user.uid, nickname, user.email);

        const profile = await getUserProfile(user.uid);
        set({ profile, isNewUser: false });
    },

    // 프로필 업데이트
    refreshProfile: async () => {
        const { user } = get();
        if (!user) return;
        const profile = await getUserProfile(user.uid);
        set({ profile });
    },

    // 로그아웃
    logout: async () => {
        const { clearChatCache } = await import('../services/chatService');
        const { clearItemCache } = await import('../services/todoService');
        const { clearSearchCache } = await import('../services/searchService');
        clearChatCache(); // 모든 채팅 캐시 삭제
        clearItemCache(); // 아이템 캐시 삭제 (localStorage + 메모리)
        clearSearchCache(); // 검색 캐시 삭제 (localStorage + 최근 검색어)
        await signOutUser();
        set({ user: null, profile: null, isNewUser: false });
    },
}));

export default useAuthStore;
